import React, {useEffect} from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { Helmet } from 'react-helmet-async';

// Component Imports
import { SectionHeading } from "../../components/SectionHeading";
import PageLayout from "../../components/PageLayout";
import HorizontalTimeline from "./Timeline";

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 my-6 md:my-10">
      <div className="flex flex-col justify-center md:col-span-2 text-center md:text-left order-2 md:order-1 mt-4 md:mt-0">
        <SectionHeading>{t('MainHero1')}</SectionHeading>
        <br />
        <p className="text-sm md:text-base">{t('MainHero2')}</p>
      </div>
      <div className="flex flex-col justify-center md:col-span-3 order-1 md:order-2">
        <img
          src="/image/taxi_freepik-removebg.png"
          alt="Taxi"
          className="w-[90%] max-w-[400px] mx-auto md:max-w-none"
        />
      </div>
    </div>
  );
};

const LogoSection: React.FC = () => {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-4 p-6 md:p-10 bg-[#EC6707] rounded-lg my-6 md:my-8">
    {/*<div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-4 p-6 md:p-10 rounded-lg my-6 md:my-8">*/}
      <img
        className="mx-auto sm:mx-0 md:col-start-2 w-32 sm:w-auto"
        src="/image/image 3.png"
        alt="Logo 1"
      />
      <img
        className="mx-auto sm:mx-0 w-32 sm:w-auto"
        src="/image/image 4.png"
        alt="Logo 2"
      />
      <img
        className="mx-auto sm:mx-0 w-32 sm:w-auto"
        src="/image/Inclusive_Org_Logo_black-e-removebg.png"
        alt="Logo 3"
      />
    </div>
  );
};

const ServiceSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4 my-6 md:my-10">
      <div className="md:col-span-2 order-2 md:order-1">
        <img
          src="/image/CN_Map.png"
          alt="Map"
          className="w-full max-w-[400px] mx-auto md:max-w-none"
        />
      </div>
      <div className="flex flex-col justify-center md:col-span-2 text-center md:text-left order-1 md:order-2 mb-4 md:mb-0">
        <SectionHeading>{t('MainService1')}</SectionHeading>
        <br />
        <p className="text-sm md:text-base">
          {t('MainService2')}
        </p>
      </div>
    </div>
  );
};

const TimelineSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col w-full h-full p-3 md:p-5">
      <SectionHeading>{t('MainMilestone')}</SectionHeading>
      <HorizontalTimeline />
    </div>
  );
};

const PageHeader: React.FC = () => {
  const {t} = useTranslation();
  return (
    <div
      className="w-full text-center mb-8 md:mb-12 py-24 bg-cover bg-center"
      style={{
        backgroundImage:
          "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/image/GBATaxi_background.jpg')",
      }}
    >
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
        {t('MainHeader')}
        <span className="block h-1 w-24 md:w-32 bg-[#EC6707] mx-auto mt-3 md:mt-4 rounded-full"></span>
      </h1>
      <div className="flex justify-center mt-5 md:mt-6">
        <div
          className="relative w-64 h-20 transition-transform duration-300 hover:scale-105 group"
        >
          <svg viewBox="0 0 240 80" className="w-full h-full">
            {/* Road - changed to white */}
            <path d="M10,60 Q120,40 230,60" stroke="white" strokeWidth="6" strokeDasharray="8 4" fill="none"/>

            {/* HK Location Pin */}
            <g transform="translate(30,35)">
              <circle cx="0" cy="0" r="12" fill="#EC6707" className="animate-pulse"/>
              <path d="M0,-15 C-8,-15 -15,-8 -15,0 C-15,10 0,25 0,25 C0,25 15,10 15,0 C15,-8 8,-15 0,-15"
                    fill="#EC6707"/>
              <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">HK</text>
            </g>

            {/* Taxi */}
            <g transform="translate(120,45)" className="transition-transform duration-1000">
              {/* Taxi body */}
              <rect x="-20" y="-10" width="40" height="15" fill="#FFDD00" rx="5"/>
              <rect x="-15" y="-20" width="30" height="10" fill="#FFDD00" rx="3"/>
              {/* Windows */}
              <rect x="-12" y="-18" width="8" height="8" fill="#333" rx="1"/>
              <rect x="4" y="-18" width="8" height="8" fill="#333" rx="1"/>
              {/* Wheels - changed to white */}
              <circle cx="-12" cy="6" r="6" fill="#333"/>
              <circle cx="12" cy="6" r="6" fill="#333"/>
              {/* Taxi Light */}
              <rect x="0" y="-23" width="5" height="3" fill="#EC6707"/>
            </g>

            {/* GBA Location Pin */}
            <g transform="translate(210,35)">
              <circle cx="0" cy="0" r="12" fill="#EC6707" className="animate-pulse"/>
              <path d="M0,-15 C-8,-15 -15,-8 -15,0 C-15,10 0,25 0,25 C0,25 15,10 15,0 C15,-8 8,-15 0,-15"
                    fill="#EC6707"/>
              <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">GBA</text>
            </g>
          </svg>

          {/* Caption Text */}
          <div className="absolute -bottom-6 left-0 right-0 text-center text-xl font-medium text-gray-300">
            {t('MainHeaderDescription')}
          </div>
        </div>
      </div>
    </div>
  );
}

const BottomBarSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="bottom-bar">
      <SectionHeading>
        <a href="https://wa.link/xzle92">
          {t("預約中港車請按此wts96877871")}
        </a>
      </SectionHeading>
    </div>
  );
};

const AdvantagesSection: React.FC = () => {
  const { t } = useTranslation();

  // YouTube videos data
  const videos = [
    {
      id: "qee1KspdDRo",
      title: t("video_title_1"),
      thumbnail: "/image/hqdefault.jpg",
      color: "#EC6707"
    },
    {
      id: "UQo7A7TgfLY",
      title: t("video_title_2"),
      thumbnail: "/image/logo/GBA_logo.png",
      color: "#2196F3"
    },
    {
      id: "2M0FmGMUPoo",
      title: t("video_title_3"),
      thumbnail: "/image/passenger_car.png",
      color: "#9C27B0"
    }
  ];

  return (
    <div className="py-16 bg-[#030B32] relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EC6707] to-transparent opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EC6707] to-transparent opacity-50"></div>

      <div className="absolute inset-0 bg-[#030B32] opacity-70 z-0"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-14">
          <SectionHeading>{t('Advantages.Heading')}</SectionHeading>
          <p className="mt-4 text-lg text-gray-300">{t('Advantages.Subheading')}</p>
        </div>

        {/* Videos Section */}
        <div className="mt-20">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((video, index) => (
              <a
                key={index}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#05164e] bg-opacity-70 backdrop-blur-sm rounded-xl shadow-lg border border-opacity-20 border-gray-500 overflow-hidden group hover:transform hover:scale-105 transition-all duration-300"
              >
                <div className="relative">
                  {/* Video thumbnail */}
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-black bg-opacity-60 flex items-center justify-center group-hover:bg-opacity-80 transition-all duration-300 group-hover:scale-110">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center border-2 relative"
                           style={{borderColor: video.color}}>
                        <svg viewBox="0 0 68 48" className="w-8 h-8">
                          <path
                            className="fill-white"
                            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                          ></path>
                          <path
                            d="M45,24 L27,14 L27,34 L45,24 Z"
                            fill="#000"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Decorative gradient corner */}
                  <div className="absolute -top-10 -right-10 w-20 h-20 rounded-full opacity-20"
                       style={{background: `radial-gradient(circle, ${video.color}, transparent 70%)`}}></div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-bold text-center text-white group-hover:text-[#EC6707] transition-colors duration-300">{video.title}</h3>
                  <div className="mt-3 flex items-center justify-center text-sm font-medium" style={{color: video.color}}>
                    {/*<span>{t('Videos.WatchNow') || 'Watch Now'}</span>*/}
                    <i className="fas fa-arrow-right ml-2 transition-transform duration-300 group-hover:translate-x-1"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <a href="/book">
            <button className="bg-gradient-to-r from-[#EC6707] to-[#f08339] hover:from-[#d05a06] hover:to-[#EC6707] text-white font-medium py-3 px-10 rounded-lg transition-all duration-300 shadow-lg hover:shadow-orange-900/30 transform hover:-translate-y-1">
              {t('BookRideNow')}
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

const MainPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/",
      title: "Main page",
    });
  }, []);
  return (
    <PageLayout>
      <Helmet>
        {/* Set HTML lang attribute based on current language */}
        <html lang={i18n.language} />
        {/* Set page title */}
        <title>{t('meta.title')}</title>
        {/* Set meta description */}
        <meta name="description" content={t('meta.description')} />
        {/* Set meta keywords */}
        <meta name="keywords" content={t('meta.keywords')} />
        {/* Optional: Open Graph tags for social media */}
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:locale" content={i18n.language === 'en' ? 'en_US' : 'zh_CN'} />
      </Helmet>
      <div className="flex justify-center items-center w-full px-4 sm:px-0 pt-20">
        <main className="flex flex-col items-center w-full sm:w-[90%] md:w-[85%] lg:w-[80%]">
          <PageHeader />
          <AdvantagesSection />
          <HeroSection />
          <LogoSection />
          <ServiceSection />
          <TimelineSection />
          <BottomBarSection />
        </main>
      </div>
    </PageLayout>
  );
};

export default MainPage;
